import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { AuthProvider } from "./contexts/AuthContext";
import { WorkoutSessionProvider } from "./contexts/WorkoutSessionContext";
import Topbar from "./components/Topbar";
import Dashboard from "./pages/Dashboard";
import WorkoutAnalysisV2 from "./pages/WorkoutAnalysisV2";
import GpxFitTraceabilityViewer from "./pages/GpxFitTraceabilityViewer";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import ChangePassword from "./pages/ChangePassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import AdminFeedbackDetail from "./pages/AdminFeedbackDetail";
import RunningDashboard from "./pages/running/RunningDashboard";
import RunningAnalysis from "./pages/running/RunningAnalysis";
import RunningProfile from "./pages/running/RunningProfile";
import RunningTraceability from "./pages/running/RunningTraceability";
import RunningAdmin from "./pages/running/RunningAdmin";
import RunningComparison from "./pages/running/RunningComparison";
import RunningPredictionAccuracy from "./pages/running/RunningPredictionAccuracy";
import RunningAccuracyReports from "./pages/running/RunningAccuracyReports";
import SharedRunningAnalysis from "./pages/running/SharedRunningAnalysis";
import SharedRunningComparison from "./pages/running/SharedRunningComparison";
import SharedLiveTracking from "./pages/running/SharedLiveTracking";
import ClubPage from "./pages/ClubPage";
import ClubJoin from "./pages/ClubJoin";
import BikeComparison from "./pages/BikeComparison";
import Help from "./pages/Help";
import Privacy from "./pages/Privacy";
import Footer from "./components/Footer";

function App() {
  const [theme, colorMode] = useMode();
  const location = useLocation();

  useEffect(() => {
    document.title = location.pathname.startsWith('/running')
      ? 'mkpace - running analysis'
      : 'mkpace - cycling analysis';
  }, [location.pathname]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <WorkoutSessionProvider>
          <CssBaseline />
          <div className="app">
            <main className="content">
              <Topbar />
              <Routes>
                {/* Running is the default module: the root lands there. The bike
                    dashboard keeps its own path so the sport toggle, the logo and
                    every "back to dashboard" in the bike pages still have somewhere
                    to go — a bare "/" would bounce straight back to running. */}
                <Route path="/" element={<Navigate to="/running" replace />} />

                {/* Bike Module Routes */}
                <Route path="/bike" element={<Dashboard />} />
                <Route path="/analysis" element={<WorkoutAnalysisV2 />} />
                <Route path="/analysis/share/:shareId" element={<WorkoutAnalysisV2 />} />
                <Route path="/compare" element={<BikeComparison />} />
                <Route path="/traceability" element={<GpxFitTraceabilityViewer />} />

                {/* Running Module Routes */}
                <Route path="/running" element={<RunningDashboard />} />
                <Route path="/running/analysis" element={<RunningAnalysis />} />
                <Route path="/running/profile" element={<RunningProfile />} />
                <Route path="/running/traceability" element={<RunningTraceability />} />
                <Route path="/running/admin" element={<RunningAdmin />} />
                <Route path="/running/compare" element={<RunningComparison />} />
                <Route path="/running/prediction-accuracy" element={<RunningAccuracyReports />} />
                <Route path="/running/prediction-accuracy/:reportId" element={<RunningPredictionAccuracy />} />
                <Route path="/running/analysis/share/:shareId" element={<SharedRunningAnalysis />} />
                <Route path="/running/compare/share/:shareId" element={<SharedRunningComparison />} />
                <Route path="/running/live/share/:shareId" element={<SharedLiveTracking />} />

                {/* Club Routes — specific before generic */}
                <Route path="/clubs/join/:inviteToken" element={<ClubJoin />} />
                <Route path="/clubs/:clubId" element={<ClubPage />} />

                {/* Manual */}
                <Route path="/help" element={<Help />} />
                <Route path="/help/:slug" element={<Help />} />

                {/* Privacy policy — public, no auth (linked from the Connect IQ Store submission) */}
                <Route path="/privacy" element={<Privacy />} />

                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/feedback/:feedbackId" element={<AdminFeedbackDetail />} />
              </Routes>
              <Footer />
            </main>
          </div>
          </WorkoutSessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
